import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { kpiApi } from '../../lib/api.js';
import { KpiCard } from './KpiCard.js';
import { Skeleton } from '../ui/Skeleton.js';
import { useAuth } from '../../lib/auth.js';
import type { KpiPeriod } from '@imza/shared';

const PERIOD_OPTIONS: Array<{ value: KpiPeriod; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: '7 days' },
  { value: 'month', label: '30 days' },
];

const STAGE_COLORS: Record<string, string> = {
  available: '#00c875',
  not_available: '#545479',
  sold: '#e2445c',
};

export function AgentDashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState<KpiPeriod>('today');

  const { data: kpi, isLoading } = useQuery({
    queryKey: ['kpi', 'agent', user?.id, period],
    queryFn: () => kpiApi.agent(user!.id, period),
    enabled: !!user,
  });

  const stageData = Object.entries(kpi?.stageDistribution ?? {}).map(([stage, count]) => ({
    name: stage.replace('_', ' '),
    value: count,
    fill: STAGE_COLORS[stage] ?? '#e0e3eb',
  }));

  const buildingsData = (kpi?.topBuildings ?? []).slice(0, 5);

  return (
    <div className="p-4 space-y-4">
      {/* Period selector */}
      <div className="flex gap-2">
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setPeriod(opt.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium min-h-[36px] transition-colors ${
              period === opt.value
                ? 'bg-primary text-white'
                : 'bg-hover-bg text-secondary hover:text-primary-text'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard
          label="Calls"
          value={kpi?.callsTotal ?? 0}
          subLabel={`${kpi?.callsAnswered ?? 0} answered`}
          isLoading={isLoading}
        />
        <KpiCard
          label="WhatsApp"
          value={kpi?.whatsappSent ?? 0}
          isLoading={isLoading}
        />
        <KpiCard
          label="Notes"
          value={kpi?.noteAdded ?? 0}
          isLoading={isLoading}
        />
        <KpiCard
          label="Contact rate"
          value={kpi ? `${Math.round(kpi.contactRate * 100)}%` : '—'}
          subLabel="answered / called"
          isLoading={isLoading}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Top buildings */}
        {isLoading ? (
          <div className="bg-white border border-border rounded-xl p-4 space-y-2">
            <Skeleton className="h-4 w-28 mb-3" />
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-7 rounded" style={{ width: `${50 + i * 12}%` }} />
            ))}
          </div>
        ) : buildingsData.length > 0 ? (
          <div className="bg-white border border-border rounded-xl p-4">
            <p className="text-sm font-semibold text-primary-text mb-3">Top buildings</p>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={buildingsData} layout="vertical" margin={{ left: 4, right: 4 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="building"
                  width={80}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v) => (v ?? '(none)').substring(0, 12)}
                />
                <Tooltip />
                <Bar dataKey="count" fill="#0071e3" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : null}

        {/* Stage distribution */}
        {isLoading ? (
          <div className="bg-white border border-border rounded-xl p-4 flex items-center justify-center">
            <Skeleton className="w-32 h-32 rounded-full" />
          </div>
        ) : stageData.length > 0 ? (
          <div className="bg-white border border-border rounded-xl p-4">
            <p className="text-sm font-semibold text-primary-text mb-3">My landlords by stage</p>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={stageData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={60}
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={false}
                >
                  {stageData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : null}
      </div>
    </div>
  );
}
